import classNames from "classnames";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { ShopHead } from "@components/CommonHead";
import CommonLayout from "@components/CommonLayout";
import { ImageFlux } from "@components/ImageFlux";
import { OverlayPhoto } from "@components/OverlayPhoto";
import { Layout, NameArea } from "@components/ShopDetail";
import type { UsersMasterShopImageType } from "@graphql/__generated__/types";
import { queryForSSP } from "@libs/apolloClient";
import { paths } from "@libs/paths";
import { masterImageId2Anchor } from "@libs/utils/photo";
import { setUuidFromQuery } from "@libs/utils/query";
import { Photos_ShopDocument as ShopDocument } from "./__generated__/queries";
import styles from "./index.module.css";
import type { Photos_ShopQueryResult as ShopQueryResult } from "./__generated__/queries";
import type { ApolloError } from "@apollo/client";
import type { GetServerSideProps, NextPage } from "next";

// 「料理・ドリンク」(masterShopImage.id = 5)だけは表示領域の都合上タブ的には「ドリンク」としている。
const tabDrink: Pick<UsersMasterShopImageType, "id" | "name"> = {
  id: 5,
  name: "ドリンク",
};

// 「カバー」画像はタブ対象外のためフィルタ用にidを定義
const idCover = 1;

type OverlayPhotoOptions = {
  opened: boolean;
  startIndex: number;
};

type PhotosProps = {
  data?: ShopQueryResult["data"];
  error?: ApolloError;
};

const ShopPhoto: NextPage<PhotosProps> = (props) => {
  const { data } = props;

  // 写真が1枚以上あるカテゴリのみ表示する（0枚のカテゴリはタブ・セクションごと出さない）
  const displayMasterShopImages: UsersMasterShopImageType[] = useMemo(
    () =>
      (data?.masterShopImages ?? []).filter((ms) =>
        (data?.shop.shopImages ?? []).some(
          (si) => si.masterShopImageId === ms.id
        )
      ),
    [data?.masterShopImages, data?.shop.shopImages]
  );

  // タブメニューとしては「カバー」は除外する & 「料理・ドリンク」は「ドリンク」とする(スペース不足のため)
  const tabs: UsersMasterShopImageType[] = useMemo(
    () =>
      displayMasterShopImages
        .map((ms) => (ms.id === tabDrink.id ? { ...ms, ...tabDrink } : ms))
        .filter((ms) => ms.id !== idCover),
    [displayMasterShopImages]
  );

  const { query } = useRouter();

  const shopUuid = useMemo(() => {
    return setUuidFromQuery(query.uuid);
  }, [query.uuid]);
  const shopHeadOverrides = {
    canonicalUrl: paths.shopPhoto(shopUuid || ""),
  };

  const [activeMasterShopImageId, setActiveMasterShopImageId] = useState(0);
  const [overlayPhotoOptions, setOverlayPhotoOptions] =
    useState<OverlayPhotoOptions>({ startIndex: 0, opened: false });

  useEffect(() => {
    // カバー以外の写真が無くタブが0件の場合は、選択状態にするタブが無いため何もしない
    if (tabs.length === 0) {
      return;
    }
    const defaultMasterShopImageId = tabs[0].id;
    if (!location.hash) {
      setActiveMasterShopImageId(defaultMasterShopImageId);
      return;
    }
    const masterShopImage = tabs.find(
      (m) => `#${masterImageId2Anchor(m.id)}` === location.hash
    );

    setActiveMasterShopImageId(masterShopImage?.id ?? defaultMasterShopImageId);
  }, [setActiveMasterShopImageId, tabs]);

  return (
    <CommonLayout hasFooter footerPC>
      <ShopHead shop={data?.shop} type="photos" overrides={shopHeadOverrides} />

      {data && (
        <div className={styles.main}>
          <div className={classNames(styles.top, "none md:block")}>
            <NameArea shop={data.shop} />
          </div>

          <Layout shop={data.shop} current="shopPhoto">
            <div
              className={classNames(styles.columnInner, "l-main__innerWidth")}
            >
              {/* カバー以外の写真が無い店舗では選べるタブが無いため、タブバーごと表示しない */}
              {tabs.length > 0 && (
                <div className={styles.btnList}>
                  <div className={styles.btnListInner}>
                    {tabs.map((m) => (
                      <div
                        key={m.id}
                        className={classNames(styles.btnListItems, {
                          [styles.isActive]: m.id === activeMasterShopImageId,
                        })}
                      >
                        <a
                          href={`#${masterImageId2Anchor(m.id)}`}
                          onClick={(): boolean => {
                            setActiveMasterShopImageId(m.id);
                            return true;
                          }}
                        >
                          {m.name}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {displayMasterShopImages.map((tab) => (
                <section
                  key={`${tab.id}`}
                  id={masterImageId2Anchor(tab.id)}
                  className={styles.inStoreSection}
                >
                  <h1 className={`${styles.contentTitle} c-contentTitle`}>
                    {tab.name}
                  </h1>
                  <div className={styles.thumbnailList}>
                    {data.shop.shopImages
                      .filter((si) => si.masterShopImageId === tab.id)
                      .map((image) => (
                        <div
                          className={styles.thumbnailListItems}
                          key={image.id}
                        >
                          <a
                            onClick={(): void => {
                              setOverlayPhotoOptions({
                                opened: true,
                                startIndex: data.shop.shopImages.findIndex(
                                  (x) => x.id === image.id
                                ),
                              });
                            }}
                          >
                            <ImageFlux
                              imageType={"thumbnail"}
                              url={image.imageUrl}
                              alt={image.imagePath}
                            />
                          </a>
                        </div>
                      ))}
                  </div>
                </section>
              ))}
            </div>
          </Layout>
        </div>
      )}

      {overlayPhotoOptions.opened && (
        <OverlayPhoto
          defaultActiveIndex={overlayPhotoOptions.startIndex}
          open={overlayPhotoOptions.opened}
          photos={(data?.shop.shopImages ?? []).map(({ id, imageUrl }) => ({
            id,
            src: imageUrl,
            alt: `店舗写真_${id}`,
          }))}
          onClose={(): void =>
            setOverlayPhotoOptions({ opened: false, startIndex: 0 })
          }
        />
      )}
    </CommonLayout>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { params } = context;
  return queryForSSP({
    query: ShopDocument,
    variables: {
      shopUuid: params?.uuid || "",
    },
  });
};

export default ShopPhoto;
